import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  Skeleton,
  Paper,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  ManageAccounts as ManageIcon,
  Print as PrintIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useStripe } from '../contexts/FirebaseStripeContext';
import NavBar from '../components/NavBar/NavBar';
import WaitlistEnrollmentMessage from '../components/shared/WaitlistEnrollmentMessage';
import WaitlistStatusChip from '../components/shared/WaitlistStatusChip';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import { useWaitlist } from '../hooks/useWaitlist';

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user, userProfile } = useSelector((state: RootState) => state.auth);
  const { subscription, hasAccess, products, isLoading, subscribe, openPortal } = useStripe();
  const [isProcessing, setIsProcessing] = useState(false);
  const { hasSubscriptionAccess, config } = useWaitlist();

  const formatDate = (timestamp: { seconds: number } | null) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSubscribe = async () => {
    if (!hasSubscriptionAccess) {
      return;
    }

    // Find the monthly price
    const monthlyProduct = products.find(p => p.name.includes('1$ Monthly'));
    const monthlyPrice = monthlyProduct?.prices.find(p => p.interval === 'month');
    
    if (!monthlyPrice) {
      console.error('No monthly price found');
      return;
    }

    setIsProcessing(true);
    try {
      await subscribe(monthlyPrice.id);
    } catch (error) {
      console.error('Error subscribing:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!hasSubscriptionAccess) {
      return;
    }

    setIsProcessing(true);
    try {
      await openPortal();
    } catch (error) {
      console.error('Error opening portal:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return { label: 'No Subscription', color: 'default' as const };
    
    switch (subscription.status) {
      case 'active':
        return { 
          label: subscription.cancel_at_period_end ? 'Active (Canceling)' : 'Active', 
          color: 'success' as const 
        };
      case 'trialing':
        return { label: 'Trial', color: 'info' as const };
      case 'past_due':
        return { label: 'Past Due', color: 'warning' as const };
      case 'canceled':
        return { label: 'Canceled', color: 'error' as const };
      default:
        return { label: subscription.status, color: 'default' as const };
    }
  };

  return (
    <>
      <NavBar />
      
      <Box sx={{ 
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        pt: 4,
        pb: 6
      }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
            {t('settings.title')}
          </Typography>

          {/* Waitlist Message for non-authorized users */}
          <WaitlistEnrollmentMessage variant="compact" />

          {/* User Profile Section */}
          <Card elevation={0} sx={{ 
            mb: 4, 
            borderRadius: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(31, 41, 55, 0.8)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon /> {t('settings.profile.title')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {userProfile?.firstName} {userProfile?.lastName}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                {t('settings.profile.language')}
              </Typography>
              
              <Box sx={{ maxWidth: 200 }}>
                <LanguageSwitcher />
              </Box>
            </CardContent>
          </Card>

          {/* Subscription Section */}
          <Card elevation={0} sx={{ 
            mb: 4, 
            borderRadius: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(31, 41, 55, 0.8)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon /> {hasSubscriptionAccess ? 'Print Subscription' : 'Access Status'}
              </Typography>

              {!hasSubscriptionAccess ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {config.MESSAGES.COMING_SOON}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      You're currently on the waitlist. At launch, you'll be able to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      {config.UPCOMING_FEATURES.map((feature, index) => (
                        <Typography key={index} component="li" variant="body2">
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </Alert>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WaitlistStatusChip />
                    <Typography variant="body2" color="text.secondary">
                      Launch date: {config.LAUNCH_DATE}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  {isLoading ? (
                    <Box>
                      <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
                      <Skeleton variant="text" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ 
                        mb: 3, 
                        p: 3, 
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(55, 65, 81, 0.5)' : 'background.default', 
                        borderRadius: 2,
                        border: theme.palette.mode === 'dark' ? '1px solid rgba(75, 85, 99, 0.3)' : 'none'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <PrintIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Unlimited Printing
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Print and download unlimited lease agreements
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            $1/mo
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Subscription Status
                          </Typography>
                          <Chip 
                            label={getSubscriptionStatus().label}
                            color={getSubscriptionStatus().color}
                            size="small"
                            icon={hasAccess ? <CheckCircleIcon /> : undefined}
                          />
                        </Box>
                        
                        {subscription && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                              {subscription.cancel_at_period_end ? 'Cancels on' : 'Next billing date'}
                            </Typography>
                            <Typography variant="body1">
                              {formatDate(subscription.current_period_end)}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {!hasAccess ? (
                          <Button
                            variant="contained"
                            startIcon={<CreditCardIcon />}
                            onClick={handleSubscribe}
                            disabled={isProcessing}
                            sx={{ textTransform: 'none' }}
                          >
                            {isProcessing ? 'Processing...' : 'Subscribe for $1/month'}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            startIcon={<ManageIcon />}
                            onClick={handleManageSubscription}
                            disabled={isProcessing}
                            sx={{ textTransform: 'none' }}
                          >
                            {isProcessing ? 'Loading...' : 'Manage Subscription'}
                          </Button>
                        )}
                      </Box>

                      {subscription?.cancel_at_period_end && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Your subscription will end on {formatDate(subscription.current_period_end)}.
                          You can continue using all features until then.
                        </Alert>
                      )}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default SettingsPage; 